import { makeAutoObservable } from 'mobx';
import apiHandler from '../handlers/ApiHandler';
import authStore from './AuthStore';
import { trackEvent } from '../handlers/analytics';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class CheckoutStore {
  name = '';
  email = '';
  billingAddress = '';
  shippingAddress = '';
  phone = '';
  paymentStatus = 'idle';
  cartStore = null;

  constructor(cartStore) {
    this.cartStore = cartStore;
    makeAutoObservable(this);
  }

  setName(name) {
    this.name = name;
  }

  setEmail(email) {
    this.email = email;
    this.captureCart();
  }

  setBillingAddress(address) {
    this.billingAddress = address;
  }

  setShippingAddress(address) {
    this.shippingAddress = address;
  }

  setPhone(phone) {
    this.phone = phone;
  }

  // Abandoned-cart capture: when we know a valid shopper email and the cart
  // has items, save the cart on the backend so it can send a recovery reminder
  // if they don't check out. Fire-and-forget; never block or break checkout.
  captureCart() {
    try {
      const email = (this.email && emailRegex.test(this.email))
        ? this.email
        : (authStore.email && emailRegex.test(authStore.email) ? authStore.email : null);

      if (!email) return;

      const items = this.cartStore ? this.cartStore.cartItems : [];
      if (!items || items.length === 0) return;

      apiHandler.post('/flows/cart', {
        email,
        username: this.name || authStore.username || undefined,
        items,
        cartUrl: 'https://earphysics.com/cart',
      }).catch(() => {});
    } catch (error) {
      // Never let cart capture interfere with checkout.
    }
  }

  // Clear the saved abandoned-cart record (e.g. after a successful purchase).
  clearSavedCart(email) {
    try {
      const recoveryEmail = email
        || (this.email && emailRegex.test(this.email) ? this.email : null)
        || (authStore.email && emailRegex.test(authStore.email) ? authStore.email : null);

      if (!recoveryEmail) return;

      apiHandler.post('/flows/cart', {
        email: recoveryEmail,
        items: [],
      }).catch(() => {});
    } catch (error) {
      // Never let cart clearing interfere with checkout.
    }
  }

  async createIntent(order, additionalDetails) {
    this.paymentStatus = 'processing';

    const intent = {
      order,
      additionalDetails
    }

    try {
      const response = await apiHandler.post('/payment/create', intent );
      this.paymentStatus = 'succeeded';
      return response;
    } catch (error) {
      this.paymentStatus = 'error';
      throw new Error('Payment processing failed');
    }
  }

  async submitOrder() {
    try {
      const order = {
        userId: authStore.user._id,
        status: 'pending',
        totalAmount: this.cartStore.total,
        currency: 'USD',
        orderNumber: `EP-${Date.now()}`,
        items: this.cartStore.cartItems.map(item => ({
          product: item._id,
          quantity: item.quantity,
          productPrice: item.price,
          productImage: item.productImage,
        })),
        shippingAddress: this.shippingAddress,
        billingAddress: this.billingAddress,
        phone: this.phone,
        description: `Order placed by ${this.name} - ${new Date().toLocaleString()}`,
      };

      trackEvent('begin_checkout', { items: order.items?.length });

      const response = await apiHandler.post('/orders/create', { order });

      if (response.data.success) {
        trackEvent('purchase', {
          orderId: response.data.order?._id,
          value: response.data.order?.totalAmount,
        });
        this.clearSavedCart(this.email);
        this.cartStore.clearCart();
        this.paymentStatus = 'completed';
        return response.data.order;
      } else {
        throw new Error('Order creation failed');
      }
    } catch (error) {
      console.error('Order submission failed:', error);
      throw new Error('Order submission failed');
    }
  }

  loadCheckoutData() {
    this.setName(localStorage.getItem('checkout_name') || '');
    this.setEmail(localStorage.getItem('checkout_email') || '');
    this.setBillingAddress(localStorage.getItem('checkout_billingAddress') || '');
    this.setShippingAddress(localStorage.getItem('checkout_shippingAddress') || '');
    this.setPhone(localStorage.getItem('checkout_phone') || '');
  }

  saveCheckoutData() {
    try {
      localStorage.setItem('checkout_name', this.name);
      localStorage.setItem('checkout_email', this.email);
      localStorage.setItem('checkout_billingAddress', this.billingAddress);
      localStorage.setItem('checkout_shippingAddress', this.shippingAddress);
      localStorage.setItem('checkout_phone', this.phone);
    } catch (error) {
      console.error('Failed to save checkout data locally:', error);
    }

    this.captureCart();
  }
}

export default CheckoutStore;
